import React, { useCallback, useMemo } from "react";
import { FlatList, View } from "react-native";
import { AHButton, AHEmptyScreen, AHSeparator, useBoxStyle } from "arkh";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { KPIAdminQuery } from "./__generated__/KPIAdminQuery.graphql";
import { KPIAdminListItem } from "./KPIAdminListItem";
import { FormattedMessage } from "react-intl";
import { Lottie } from "../../../components/Lottie";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { StoreAdminNavigationScreenProps } from "../../StoreAdmin/StoreAdminNavigation";
import { KPIAdminListItem_Routine$key } from "./__generated__/KPIAdminListItem_Routine.graphql";
import { ActionFooter } from "@/src/components/ActionFooter";
import { SafeAreaView } from "react-native-safe-area-context";

type Props = StoreAdminNavigationScreenProps<"KPIAdmin">;

function KPIAdminComponent(props: {
  kpis: readonly (KPIAdminListItem_Routine$key & {
    title: string;
    id: string;
  })[];
  openKPIEdit: (routineId: string, title: string) => void;
  openKPICreate: () => void;
  openWebView: (url: string) => void;
}) {
  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  const routines = useMemo(() => {
    return (
      props.kpis?.slice().sort((a, b) => a.title.localeCompare(b.title)) ?? []
    );
  }, [props.kpis]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof routines)[number]; index: number }) => (
      <KPIAdminListItem
        kpi={item}
        onPress={() => {
          props.openKPIEdit(item.id, item.title);
        }}
        isFirst={index === 0}
        isLast={index === routines.length - 1}
      />
    ),
    [props, routines.length]
  );

  const intl_token = useIntlTokens();

  if (routines.length === 0) {
    return (
      <SafeAreaView style={{ flex: 1 }}>
        <AHEmptyScreen
          title={
            <FormattedMessage
              description="KPI Admin Empty Screen Title"
              defaultMessage="Let’s start with KPIs!"
            />
          }
          subtitle={
            <FormattedMessage
              description="KPI Admin Empty Screen Subtitle"
              defaultMessage={
                "Key performance indicators provide a quick look at things like 'wastage' and 'shelf gaps,' making it easy to pinpoint areas for improvement."
              }
            />
          }
          illustration={
            <Lottie
              autoPlay
              loop
              source={require("../../../assets/shake-a-empty-box.json")}
            />
          }
          cta={
            <AHButton
              themeType="primary"
              iconType="KPI"
              iconRight
              title={
                <FormattedMessage
                  description="KPI Admin Empty Screen Create KPI Button"
                  defaultMessage="Create KPI"
                />
              }
              onPress={() => props.openKPICreate()}
            />
          }
          helpLinks={
            <>
              <AHEmptyScreen.Link
                text={intl_token.LearnMore}
                icon="learn-more"
                onPress={() => {
                  props.openWebView(
                    "https://storesprint.com/support/direct/kpi-create"
                  );
                }}
              />
            </>
          }
        />
      </SafeAreaView>
    );
  }
  return (
    <View style={{ flex: 1 }}>
      <FlatList
        contentContainerStyle={contentContainerStyle}
        ItemSeparatorComponent={AHSeparator}
        data={routines}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
      />
      <ActionFooter
        safeInsetBottom
        primary={
          <ActionFooter.Primary
            title={
              <FormattedMessage
                defaultMessage="Create KPI"
                description="KPI create button"
              />
            }
            icon="KPI"
            onPress={() => {
              props.openKPICreate();
            }}
          />
        }
      />
    </View>
  );
}

const KPI_ADMIN_QUERY = graphql`
  query KPIAdminQuery {
    store {
      id
      routines(type: KPI) {
        id
        title
        ...KPIAdminListItem_Routine
      }
    }
  }
`;

function InnerKPIAdmin(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<KPIAdminQuery>(
    KPI_ADMIN_QUERY,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <KPIAdminComponent
      kpis={data.store.routines ?? []}
      openWebView={(url) => {
        props.navigation.navigateDeprecated("WebView", {
          url,
        });
      }}
      openKPICreate={() => {
        props.navigation.navigateDeprecated("KPICreate", {});
      }}
      openKPIEdit={(routineId, title) =>
        props.navigation.navigateDeprecated("KPIEdit", {
          screen: "KPIEditMain",
          params: {
            routineId,
            title,
          },
        })
      }
    />
  );
}

export function KPIAdmin(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerKPIAdmin {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
